<!-- Storage "Projects" cell: for one uploaded file (storage image), shows how many projects
     the image was added to and, on click, opens a popup listing them. Fetches the image's
     instances once (cached per abstract image) via the canonical
     ImageInstance.fetchAllByAbstractImage retrieval; the accessible/inaccessible split is
     computed against the user's accessible-projects map so it reacts when that map loads. -->
<template>
  <div class="ufp-cell">
    <span v-if="!uFile.image" class="ufp-na" :title="$t('not-converted-yet')">—</span>
    <span v-else-if="loading" class="ufp-loading"><i class="fas fa-circle-notch fa-spin"></i></span>
    <button v-else class="ufp-btn" :class="{zero: total === 0}" :title="$t('view-projects')" @click="emitOpen">
      <i class="fas fa-layer-group"></i>
      <span>{{ total }}</span>
    </button>
  </div>
</template>

<script>
import {ImageInstance} from '@/api';

// Cache instance records per abstract image so the table's periodic refresh (which
// recreates row cells) doesn't re-hit the API for already-resolved images.
const instancesCache = new Map();

export default {
  name: 'uploaded-file-projects-cell',
  props: {
    uFile: {type: Object, required: true},
    // {projectId: projectName} of the projects the current user can access
    accessibleProjects: {type: Object, default: () => ({})}
  },
  data() {
    return {
      loading: false,
      instances: instancesCache.get(this.uFile.image) || null
    };
  },
  computed: {
    distinctProjectIds() {
      if (!this.instances) {
        return [];
      }
      return [...new Set(this.instances.filter(i => i.project != null).map(i => i.project))];
    },
    total() {
      return this.distinctProjectIds.length;
    },
    // first instance per accessible project (for the link to the image in that project)
    accessible() {
      let perProject = {};
      (this.instances || []).forEach(i => {
        if (i.project != null && perProject[i.project] === undefined && this.accessibleProjects[i.project] != null) {
          perProject[i.project] = i;
        }
      });
      return Object.keys(perProject).map(pid => ({
        projectId: Number(pid),
        projectName: this.accessibleProjects[pid],
        instanceId: perProject[pid].id,
        instanceFilename: perProject[pid].instanceFilename
      })).sort((a, b) => String(a.projectName).localeCompare(String(b.projectName)));
    },
    inaccessibleCount() {
      return this.distinctProjectIds.filter(pid => this.accessibleProjects[pid] == null).length;
    }
  },
  methods: {
    async load() {
      if (!this.uFile.image || this.instances) {
        return; // not converted, or already have the records (from cache)
      }
      this.loading = true;
      try {
        let instances = await ImageInstance.fetchAllByAbstractImage(this.uFile.image);
        instancesCache.set(this.uFile.image, instances);
        this.instances = instances;
      } catch (error) {
        console.log(error);
        this.instances = [];
      }
      this.loading = false;
    },
    emitOpen() {
      this.$emit('open', {
        fileName: this.uFile.originalFilename,
        total: this.total,
        accessible: this.accessible,
        inaccessibleCount: this.inaccessibleCount
      });
    }
  },
  created() {
    this.load();
  }
};
</script>

<style scoped>
.ufp-cell { display: flex; align-items: center; justify-content: center; }
.ufp-na { color: #cbd2dd; }
.ufp-loading { color: #94a3b8; font-size: 13px; }
.ufp-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #dbe6fb;
  background: #f4f8ff;
  color: #1f6fe5;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.14s;
  font-variant-numeric: tabular-nums;
}
.ufp-btn .fas { font-size: 12px; padding-right: 0; }
.ufp-btn:hover { background: #eaf1fe; border-color: #1f6fe5; }
.ufp-btn.zero {
  border-color: #e6eaf0;
  background: #fff;
  color: #94a3b8;
}
.ufp-btn.zero:hover { background: #f8fafc; border-color: #cdd6e4; }
</style>
